<template>
  <v-container fluid>
    <div v-if="loading">
      <v-row>
        <v-col>
          <v-skeleton-loader type="card" />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-skeleton-loader type="card" />
        </v-col>
        <v-col>
          <v-skeleton-loader type="card" />
        </v-col>
      </v-row>
    </div>
    <div v-else-if="location">
      <v-row>
        <v-col>
          <v-toolbar>
            <v-app-bar-nav-icon to="/locations" exact>
              <v-icon>
                mdi-arrow-left
              </v-icon>
            </v-app-bar-nav-icon>
            <v-toolbar-title>
              {{ location.title }}
            </v-toolbar-title>
            <v-spacer />
          </v-toolbar>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-card>
            <v-toolbar flat>
              <v-toolbar-title>
                {{ $tc('locationDetail.casesHeader', items.length) }}
              </v-toolbar-title>
              <v-spacer />
              <v-text-field
                v-model="searchInput"
                prepend-inner-icon="mdi-magnify"
                dense
                solo
                hide-details
              />
            </v-toolbar>
            <v-data-table
              :headers="headers"
              :items="items"
              :loading="loadingCases"
              :search="searchInput"
              sort-by="modifiedOn"
              sort-desc
              @click:row="({ route }) => $router.push(route)"
            >
              <template #[`item.code`]="{ value, item }">
                <div class="d-flex align-center">
                  <tpa-case-icon :type="getType(item)" size="x-small" />
                  <tt class="ml-2">
                    {{ value }}
                  </tt>
                </div>
              </template>
              <template #[`item.type`]="{ item }">
                <span v-if="getType(item) === 'support'">
                  {{ item.categoryTitle }}
                </span>
                <span v-else-if="getType(item) === 'delivery'">
                  {{ item.typeTitle }}
                </span>
              </template>
              <template #[`item.createdOn`]="{ value }">
                <tt>
                  {{ value | formatDate }}
                </tt>
              </template>
              <template #[`item.modifiedOn`]="{ value }">
                <tt>
                  {{ value | formatDateTime }}
                </tt>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-card>
            <v-toolbar flat>
              <v-toolbar-title>
                {{ $t('common.details') }}
              </v-toolbar-title>
              <v-spacer />
              <LocationEditDialog :initial-location="location" @save:success="fetchData" />
            </v-toolbar>
            <v-card-text>
              <v-list dense>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>
                      mdi-earth
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ location.country.name ?? $t('common.notSet') }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>
                      mdi-selection-marker
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ location.area?.name ?? $t('common.notSet') }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>
                      mdi-pin-outline
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ !!location.address ? location.address : $t('common.notSet') }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>
                      mdi-shape-outline
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ location.type?.title ?? $t('common.notSet') }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item v-if="$store.getters.isInternal">
                  <v-list-item-icon>
                    <v-icon>
                      mdi-text
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ location.notes ?? $t('locationDetail.noNotes') }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider />
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>
                      mdi-account-outline
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ location.contactName ?? $t('common.notSet') }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>
                      mdi-email-outline
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ location.contactEmail ?? $t('common.notSet') }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>
                      mdi-phone-outline
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ location.contactPhone ?? $t('common.notSet') }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col>
          <ActivityLog
            relation-kind="location"
            :relationId="$route.params.locationId"
            :entries="messages"
          />
        </v-col>
      </v-row>
    </div>
    <v-alert v-else type="warning" text>
      {{ $t('errors.default') }}
    </v-alert>
  </v-container>
</template>

<script>
import ActivityLog from '@/components/ActivityLog.vue';
import LocationEditDialog from '@/components/LocationEditDialog.vue';

export default {
  name: 'LocationDetailView',
  components: {
    ActivityLog,
    LocationEditDialog
  },
  data() {
    return {
      loading: true,
      loadingCases: true,
      location: null,
      searchInput: null,
      deliveries: [],
      supportTickets: [],
      taskRequests: [],
      messages: []
    }
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t('common.name'),
          value: 'title'
        },
        {
          text: this.$t('common.code'),
          value: 'code',
        },
        {
          text: this.$tc('common.type'),
          value: 'type'
        },
        {
          text: this.$tc('common.status'),
          value: 'statusTitle'
        },
        {
          text: this.$t('common.createdOn'),
          value: 'createdOn'
        },
        {
          text: this.$t('common.modifiedOn'),
          value: 'modifiedOn',
          align: 'right'
        }
      ]
    },
    items() {
      const result = [];

      this.deliveries.forEach(item => {
        result.push({
          ...item,
          type: this.$tc('common.delivery'),
          route: `/delivery/${item.id}`
        })
      });

      this.supportTickets.forEach(item => {
        result.push({
          ...item,
          type: this.$tc('common.supportTicket'),
          route: `/supportticket/${item.id}`
        })
      });

      this.taskRequests.forEach(item => {
        result.push({
          ...item,
          type: this.$tc('common.taskRequest'),
          route: `/taskrequest/${item.id}`
        })
      });

      return result;
    }
  },
  mounted: async function() {
    await this.fetchData();
    this.getCases();
    this.messages = await this.$store.dispatch('messages/getMessages', [ this.$route.params.locationId ]);
  },
  methods: {
    fetchData: async function() {
      try {
        const { data } = await this.$api.get(`locations/${this.$route.params.locationId}`);

        this.location = data;
      } catch (error) {
        this.$notifier.showError({});
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    getCases: async function() {
      this.loadingCases = true;

      try {
        const requests = [this.getDeliveries(), this.getSupportTickets(), this.getTaskRequests()];

        await Promise.all(requests);
      } finally {
        this.loadingCases = false;
      }
    },
    getDeliveries: async function() {
      try {
        const { data } = await this.$api.post('delivery/listsearch', {
          locations: [
            {
              value: this.$route.params.locationId
            }
          ],
          includeClosed: false
        })

        this.deliveries = data.data?.deliveries;
      } catch (error) {
        this.$notifier.showError({})
        console.error(error)
      }
    },
    getSupportTickets: async function() {
      try {
        const { data } = await this.$api.post('supportticket/listsearch', {
          locations: [
            {
              value: this.$route.params.locationId
            }
          ]
        })

        this.supportTickets = data.data?.supportTickets;
      } catch (error) {
        this.$notifier.showError({})
        console.error(error)
      }
    },
    getTaskRequests: async function() {
      try {
        const { data } = await this.$api.post('taskrequest/listsearch', {
          locations: [
            {
              value: this.$route.params.locationId
            }
          ]
        })

        this.taskRequests = data.data?.taskRequests;
      } catch (error) {
        this.$notifier.showError({})
        console.error(error)
      }
    },
    getType(item) {
      const keys = Object.keys(item);

      if (keys.includes('deliveryDescriptionApprovedOn')) {
        return 'delivery'
      }
      else if (keys.includes('calledById')) {
        return 'support'
      }
      else {
        return 'backlog'
      }
    },
    onClickRow: function({ id }) {
      this.$router
    }
  }
}
</script>

<style>
.v-data-table tr { cursor: pointer; }
</style>
